import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { RevOpsLogo } from '@/components/brand/RevOpsLogo';
import { useNavigate } from 'react-router-dom';

export function MarketingNav({ forceSolid = false }: { forceSolid?: boolean }) {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const isLight = true;
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleLang = () => {
    setLanguage(language === 'en' ? 'pt-BR' as Language : 'en' as Language);
  };

  const navLinks = [
    { label: t('landing.nav.features'), href: '/features' },
    { label: t('landing.nav.integrations'), href: '/integrations' },
    { label: t('landing.nav.pricing'), href: '/pricing' },
    { label: t('landing.footer.blog'), href: '/blog' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isLight
          ? 'bg-white/95 shadow-md backdrop-blur supports-[backdrop-filter]:bg-white/90 border-b border-[hsl(220,13%,90%)]'
          : 'bg-transparent'
      }`}
    >
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center">
          <RevOpsLogo size="md" variant={isLight ? 'dark' : 'light'} />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium transition-colors hover:opacity-80 ${
                isLight ? 'text-[#1A1F3C]' : 'text-white/90'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`text-sm font-medium px-2 py-1 rounded transition-colors ${
              isLight ? 'text-[#1A1F3C]/70 hover:text-[#1A1F3C]' : 'text-white/70 hover:text-white'
            }`}
          >
            {language === 'en' ? 'PT' : 'EN'}
          </button>
          {isAuthenticated ? (
            <>
              <Button asChild className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                <Link to="/executive-overview">
                  {t('landing.nav.accessSystem')}
                </Link>
              </Button>
              <Button variant="ghost" onClick={async () => { await signOut(); navigate('/'); }} className={isLight ? 'text-[#1A1F3C]' : 'text-white hover:bg-white/10'}>
                {t('landing.nav.logout')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" asChild className={isLight ? 'text-[#1A1F3C]' : 'text-white hover:bg-white/10'}>
                <Link to="/login">{t('landing.nav.login')}</Link>
              </Button>
              <Button asChild className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                <Link to="/signup">{t('landing.nav.startTrial')}</Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? (
            <X className={`h-6 w-6 ${isLight ? 'text-[#1A1F3C]' : 'text-white'}`} />
          ) : (
            <Menu className={`h-6 w-6 ${isLight ? 'text-[#1A1F3C]' : 'text-white'}`} />
          )}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[hsl(220,13%,90%)] shadow-lg">
          <div className="container py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[#1A1F3C] text-sm font-medium py-2"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <hr className="border-[hsl(220,13%,90%)]" />
            <button
              onClick={toggleLang}
              className="text-[#1A1F3C]/70 text-sm font-medium py-2 text-left"
            >
              {language === 'en' ? '🇧🇷 Português' : '🇺🇸 English'}
            </button>
            {isAuthenticated ? (
              <>
                <Button asChild className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white w-full">
                  <Link to="/executive-overview" onClick={() => setMobileOpen(false)}>
                    {t('landing.nav.accessSystem')}
                  </Link>
                </Button>
                <Button variant="ghost" className="w-full text-[#1A1F3C]" onClick={async () => { setMobileOpen(false); await signOut(); navigate('/'); }}>
                  {t('landing.nav.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-[#1A1F3C] text-sm font-medium py-2" onClick={() => setMobileOpen(false)}>
                  {t('landing.nav.login')}
                </Link>
                <Button asChild className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white w-full">
                  <Link to="/signup" onClick={() => setMobileOpen(false)}>{t('landing.nav.startTrial')}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
